import * as Sentry from '@sentry/react'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store/auth-store'

const { VITE_API_URL } = import.meta.env

const client = axios.create({
	baseURL: VITE_API_URL,
	withCredentials: true, // send HTTP-only auth cookies automatically
	// Mobile networks in the field are slow; without a limit requests hang forever.
	timeout: 30_000,
	headers: {
		'Content-Type': 'application/json',
	},
})

// Errors expected as part of normal flow — logging them to Sentry would just be noise.
const IGNORED_ERROR_URLS = ['/auth/refresh']

// Timeouts and dropped connections come from the client's network (field devices
// on mobile data), not from a bug in the app — logging them as errors is noise.
const NETWORK_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK']

function isIgnoredApiError(error: AxiosError): boolean {
	const url = error.config?.url ?? ''
	const status = error.response?.status

	// 401 is the auth-refresh trigger, handled below; not a real failure by itself.
	if (status === 401) return true

	if (!error.response && NETWORK_ERROR_CODES.includes(error.code ?? '')) {
		return true
	}

	return IGNORED_ERROR_URLS.some((ignored) => url.includes(ignored))
}

function reportApiErrorToSentry(error: AxiosError) {
	if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') return
	if (isIgnoredApiError(error)) return

	Sentry.withScope((scope) => {
		scope.setTag('api_url', error.config?.url)
		scope.setTag('api_method', error.config?.method)
		scope.setTag('status_code', error.response?.status)
		scope.setExtra('response_body', error.response?.data)
		scope.setExtra('request_body', error.config?.data)

		Sentry.captureException(error)
	})
}

// On 401, attempt token refresh; only logout if refresh also fails
let isRefreshing = false
let pendingQueue: Array<{
	resolve: () => void
	reject: (err: unknown) => void
}> = []

const flushQueue = (error: unknown) => {
	for (const pending of pendingQueue) {
		if (error) {
			pending.reject(error)
		} else {
			pending.resolve()
		}
	}
	pendingQueue = []
}

client.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		reportApiErrorToSentry(error)

		const publicPaths: string[] = [
			ROUTES.PUBLIC.LOGIN,
			ROUTES.PUBLIC.FORCE_CHANGE_PASSWORD,
			ROUTES.PUBLIC.RECOVER_PASSWORD,
		]
		const isPublicPage = publicPaths.includes(window.location.pathname)

		// /auth/refresh must not trigger the refresh loop
		const isRefreshCall = error.config?.url?.includes('/auth/refresh')

		if (error.response?.status !== 401 || isPublicPage || isRefreshCall) {
			return Promise.reject(error)
		}

		// Another request is already refreshing — queue this one
		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				pendingQueue.push({
					resolve: () => resolve(client(error.config!)),
					reject,
				})
			})
		}

		isRefreshing = true

		try {
			await client.post('/auth/refresh')
			flushQueue(null)
			return client(error.config!)
		} catch (refreshError) {
			flushQueue(refreshError)
			useAuthStore.getState().logout()
			window.location.href = ROUTES.PUBLIC.LOGIN
			return Promise.reject(refreshError)
		} finally {
			isRefreshing = false
		}
	},
)

export default client
